import { AutoComplete, Input, Select, Tooltip } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useRef, useState } from 'react';
import {
	fieldMappingKind,
	fieldMappingRewrite,
	fieldMappingsToRows,
	makeFieldMapping,
	readFieldMapping,
	rowsToFieldMappings,
	setFieldMappingBase,
	setFieldMappingRewrite,
} from 'src/_shared/services/job-sync/serde';
import { AUTO_PASSTHROUGH_FIELDS } from 'src/_shared/services/job-sync/passthroughFields';
import Section from '../console/Section.jsx';
import RewriteOpsEditor from './RewriteOpsEditor.jsx';

const KIND_OPTIONS = [
	{ value: 'from', label: 'field' },
	{ value: 'const', label: 'constant' },
	{ value: 'ruleSet', label: 'rule set' },
];

// Editable `fieldMappings` rows: each maps an Erin dot-path target (department.name, location.city)
// to a source — a normalized field `{from}`, a `{const}`, or a named `{ruleSet}` — rendered as
// `target ← [kind] source` per the design. Dot-path keys stay literal strings; row order is
// preserved. Rows are held locally with stable ids so editing a key doesn't reshuffle inputs; every
// change recomputes the record and lifts it via `onChange`.
export default function FieldMappingsEditor({ fieldMappings, ruleSetNames = [], fieldOptions = [], onChange }) {
	const seq = useRef(0);
	const [rows, setRows] = useState(() =>
		fieldMappingsToRows(fieldMappings).map((r) => ({ ...r, id: (seq.current += 1) }))
	);

	const commit = (nextRows) => {
		setRows(nextRows);
		onChange(rowsToFieldMappings(nextRows));
	};

	const updateRow = (id, partial) => commit(rows.map((r) => (r.id === id ? { ...r, ...partial } : r)));
	const removeRow = (id) => commit(rows.filter((r) => r.id !== id));
	const addRow = () =>
		commit([...rows, { id: (seq.current += 1), key: '', mapping: makeFieldMapping('from', '') }]);

	return (
		<Section
			id="sec-mappings"
			title="Map"
			description={
				<>
					<span className="js-mono">{AUTO_PASSTHROUGH_FIELDS.join(', ')}</span> are auto-mapped — only
					reshaped fields are listed.
				</>
			}
		>
			<div>
				{rows.length === 0 && (
					<div className="ei-empty-inline">
						No field mappings yet — the transform still auto-fills the pass-through fields above, plus the
						defaults below.
					</div>
				)}
				{rows.map((row) => {
					const kind = fieldMappingKind(row.mapping);
					const { value } = readFieldMapping(row.mapping);
					const rewriteOps = fieldMappingRewrite(row.mapping);
					return (
						<div className="js-map-rowgroup" key={row.id}>
							<div className="js-map-row">
								<div className="js-map-target js-token">
									<Input
										value={row.key}
										onChange={(e) => updateRow(row.id, { key: e.target.value })}
										placeholder="erin.field.path"
									/>
								</div>
								<span className="js-map-arrow" aria-hidden>
									←
								</span>
								<div className="js-map-source">
									<div className={`js-token js-kind${kind === 'ruleSet' ? ' is-ruleset' : ''}`}>
										<Select
											value={kind}
											onChange={(nextKind) =>
												updateRow(row.id, {
													mapping: setFieldMappingBase(row.mapping, nextKind, nextKind === 'const' ? value : value == null ? '' : String(value)),
												})
											}
											options={KIND_OPTIONS}
											style={{ width: '100%' }}
											dropdownMatchSelectWidth={false}
										/>
									</div>
									<div className={`js-token${kind === 'ruleSet' ? ' js-source-accent' : ''}`} style={{ flex: 1 }}>
										{kind === 'ruleSet' ? (
											<Select
												value={value || undefined}
												onChange={(v) => updateRow(row.id, { mapping: setFieldMappingBase(row.mapping, 'ruleSet', v) })}
												options={ruleSetNames.map((n) => ({ value: n }))}
												placeholder="rule set"
												style={{ width: '100%' }}
												status={value && !ruleSetNames.includes(value) ? 'error' : undefined}
											/>
										) : kind === 'from' ? (
											<AutoComplete
												value={value == null ? '' : String(value)}
												options={fieldOptions.map((f) => ({ value: f }))}
												filterOption={(input, option) =>
													String(option.value).toLowerCase().includes(input.toLowerCase())
												}
												onChange={(v) => updateRow(row.id, { mapping: setFieldMappingBase(row.mapping, 'from', v) })}
												placeholder="normalized field"
												style={{ width: '100%' }}
											/>
										) : (
											<Input
												value={value == null ? '' : String(value)}
												onChange={(e) => updateRow(row.id, { mapping: setFieldMappingBase(row.mapping, 'const', e.target.value) })}
												placeholder="constant value"
											/>
										)}
									</div>
									<Tooltip title="Remove mapping">
										<button
											type="button"
											className="js-iconbtn js-danger"
											onClick={() => removeRow(row.id)}
											aria-label="Remove mapping"
										>
											<DeleteOutlined />
										</button>
									</Tooltip>
								</div>
							</div>
							<div className="js-map-rewrite">
								<RewriteOpsEditor
									ops={rewriteOps}
									onChange={(nextOps) => updateRow(row.id, { mapping: setFieldMappingRewrite(row.mapping, nextOps) })}
								/>
							</div>
						</div>
					);
				})}
			</div>

			<div className="js-card-foot">
				<button type="button" className="js-linkbtn" onClick={addRow}>
					<PlusOutlined /> Add mapping
				</button>
				<span className="js-muted">nested targets like department.name are literal keys</span>
			</div>
		</Section>
	);
}
